import { ExternalLink, Calendar, User, ArrowRight, Award, BookOpen } from 'lucide-react';
import { Card } from '@/components/ui/card';
import pairedSilence from '@/assets/paired-silence.jpg';
import quietAssemblage from '@/assets/quiet-assemblage.jpg';
import greenSculpturalLamp from '@/assets/green-sculptural-lamp.jpg';
import artistPresenting from '@/assets/artist-presenting-works.jpg';
import artistWithCollection from '@/assets/artist-with-collection.jpg';
import ceramicCollection from '@/assets/ceramic-collection.jpg';
import ceramicVesselsDark from '@/assets/ceramic-vessels-dark.jpg';
import earthenEchoes from '@/assets/earthen-echoes-1.jpg';
import ceramicSculptureGreen from '@/assets/ceramic-sculpture-green.jpg';
import artistAtWheel from '@/assets/artist-at-wheel.jpg';

interface NewsArticle {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  source: string;
  externalUrl: string;
  image: string;
  category: string;
  featured?: boolean;
}

const newsArticles: NewsArticle[] = [
  {
    id: 'paired-silence-review',
    title: "The Architecture of Quiet: Critical Review Of Umar Abdulrasheed's Paired Silence",
    excerpt: "The deeply reflective piece Paired Silence by Umar Abdulrasheed places ceramic sculpture into the emotional framework of interpersonal relationships. The piece, which is made up of two upright ceramic jars, falls somewhere between sculpture and encounter, object and presence.",
    author: "Emmanuel Iduma",
    date: "February 6, 2022",
    source: "Independent Nigeria",
    externalUrl: "https://independent.ng/the-architecture-of-quiet-critical-review-of-umar-abdulrasheeds-paired-silence/",
    image: pairedSilence,
    category: "Art Review",
    featured: true,
  },
  {
    id: 'sculptural-lamp-sage',
    title: "Where Clay Becomes Light: Umar Abdulrasheed's Sculptural Lamp, Sage",
    excerpt: "Umar Abdulrasheed's Sculptural Lamp Sage provides a unique and purposeful counterbalance in a time where speed, spectacle, and digital excess are becoming more prevalent. The piece makes no effort to compete with the visual cacophony of modern society.",
    author: "Olu Ajayi",
    date: "May 18, 2024",
    source: "PM News Nigeria",
    externalUrl: "https://pmnewsnigeria.com/2024/05/18/where-clay-becomes-light-umar-abdulrasheeds-sculptural-lamp-sage/",
    image: greenSculpturalLamp,
    category: "Art Review",
    featured: false,
  },
  {
    id: 'quiet-assemblage-review',
    title: "A Critical Review of Umar Abdulrasheed's Ceramic Practice",
    excerpt: "The collection of work Quiet Assemblage by Umar Abdulrasheed shows how modern ceramics may occupy a profoundly contemplative, intellectual, and environmental area in addition to being useful and decorative. The work's authority comes from precisely this restriction.",
    author: "Yinka Elujoba",
    date: "2024",
    source: "Nigerian Tribune",
    externalUrl: "https://tribuneonlineng.com/a-critical-review-of-umar-abdulrasheeds-ceramic-practice/",
    image: quietAssemblage,
    category: "Art Review",
    featured: false,
  },
  {
    id: 'twice-through-the-fire',
    title: "Twice Through the Fire: A Reckoning with Umar Abdulrasheed's Two Ceramic Vessels",
    excerpt: "A reflective Gambian press feature examining two ceramic vessels by Umar Abdulrasheed, exploring how fire, form, and memory converge in his sculptural practice.",
    author: "The Standard",
    date: "",
    source: "The Standard Newspaper | Gambia",
    externalUrl: "https://standard.gm/art/",
    image: ceramicVesselsDark,
    category: "Art Review",
    featured: false,
  },
  {
    id: 'eko-heritage-sun',
    title: "Umar Abdulrasheed Nominated for 2026 Eko Heritage Award",
    excerpt: "Umar Abdulrasheed has been nominated for the 2026 Eko Heritage Award, recognising his contribution to contemporary ceramics and cultural practice.",
    author: "The Sun Nigeria",
    date: "June 28, 2026",
    source: "The Sun Newspaper",
    externalUrl: "https://thesun.ng/umar-abdulrasheed-nominated-for-2026-eko-heritage-award/",
    image: artistPresenting,
    category: "Award",
    featured: false,
  },
  {
    id: 'eko-heritage-vanguard',
    title: "Umar Abdulrasheed Nominated for 2026 Eko Heritage Award",
    excerpt: "The Vanguard reports on Umar Abdulrasheed's nomination for the 2026 Eko Heritage Award, celebrating his sustained impact on Nigerian ceramic art.",
    author: "Vanguard Nigeria",
    date: "May 13, 2026",
    source: "The Vanguard",
    externalUrl: "https://www.vanguardngr.com/2026/06/umar-abdulrasheed-nominated-for-2026-eko-heritage-award/",
    image: artistWithCollection,
    category: "Award",
    featured: false,
  },
  {
    id: 'between-vessel-and-sculpture',
    title: "Between Vessel and Sculpture: Umar Abdulrasheed's Contemporary Ceramics",
    excerpt: "People of Print surveys the space between vessel and sculpture in Umar Abdulrasheed's contemporary ceramics, tracing the dialogue of utility, form, and artistic intent.",
    author: "People of Print",
    date: "April 16, 2024",
    source: "People of Print",
    externalUrl: "https://peopleofprint.com/exhibition/between-vessel-and-sculpture-umar-abdulrasheeds-contemporary-ceramics",
    image: ceramicCollection,
    category: "Exhibition",
    featured: false,
  },
  {
    id: 'pivot-yiyi',
    title: "Pivot (Yiyi)",
    excerpt: "The Voice Gambia features Pivot (Yiyi), reflecting on Umar Abdulrasheed's ceramic language and the cultural currents shaping his work.",
    author: "The Voice Gambia",
    date: "February 16, 2023",
    source: "The Voice | Gambia",
    externalUrl: "https://www.voicegambia.com/pivot-yiyi/",
    image: earthenEchoes,
    category: "Feature",
    featured: false,
  },
  {
    id: 'monolith-okuta-kan',
    title: "Umar Abdulrasheed's Monolith (Okuta Kan): When Ile-Ife Clay Refuses to Be a Vessel",
    excerpt: "The News Nigeria examines Monolith (Okuta Kan), a work in which Ile-Ife clay is pushed beyond the vessel form into a commanding sculptural statement.",
    author: "The News Nigeria",
    date: "February 16, 2023",
    source: "The News Nigeria",
    externalUrl: "https://thenewsnigeria.com.ng/2023/02/16/umar-abdulrasheeds-monolith-okuta-kan-when-ile-ife-clay-refuses-to-be-a-vessel/",
    image: ceramicSculptureGreen,
    category: "Art Review",
    featured: false,
  },
  {
    id: 'crafts-council-directory',
    title: "Terrravia Ceramics on the Crafts Council Directory",
    excerpt: "Terrravia Ceramics is listed on the Crafts Council UK directory, connecting the studio's hand-built ceramic practice with collectors, curators, and craft audiences.",
    author: "Crafts Council UK",
    date: "",
    source: "Crafts Council UK",
    externalUrl: "https://www.craftscouncil.org.uk/directory/terrravia-ceramics",
    image: artistAtWheel,
    category: "Directory",
    featured: false,
  },
];

const News = () => {
  const featuredArticle = newsArticles.find(article => article.featured);
  const otherArticles = newsArticles.filter(article => !article.featured);

  return (
    <div className="py-20 min-h-screen">
      {/* Hero Section */}
      <section className="container mx-auto px-4 mb-16">
        <div className="max-w-4xl mx-auto text-center text-reveal">
          <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full mb-6">
            Press & Media
          </span>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-primary mb-6">
            News
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Featured articles, reviews, awards, and media coverage of Terravia Ceramics
          </p>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && (
        <section className="container mx-auto px-4 mb-20">
          <div className="max-w-6xl mx-auto">
            <a 
              href={featuredArticle.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <Card className="overflow-hidden border-0 shadow-2xl hover:shadow-3xl transition-all duration-500 bg-card">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  {/* Image Section */}
                  <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto lg:min-h-[500px]">
                    <img
                      src={featuredArticle.image}
                      alt={featuredArticle.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-black/20" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-full uppercase tracking-wider">
                        Featured
                      </span>
                    </div>
                  </div>
                  
                  {/* Content Section */}
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 bg-secondary text-secondary-foreground text-xs font-medium rounded-full">
                        {featuredArticle.category}
                      </span>
                    </div>
                    
                    <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors leading-tight">
                      {featuredArticle.title}
                    </h2>
                    
                    <p className="text-muted-foreground text-lg leading-relaxed mb-6 line-clamp-3">
                      {featuredArticle.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{featuredArticle.author}</span>
                      </div>
                      {featuredArticle.date && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{featuredArticle.date}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <ExternalLink className="w-4 h-4" />
                        <span>{featuredArticle.source}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-primary font-semibold group-hover:gap-4 transition-all">
                      <span>Read Full Article</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </Card>
            </a>
          </div>
        </section>
      )}

      {/* Other Articles Grid */}
      {otherArticles.length > 0 && (
        <section className="container mx-auto px-4 mb-20">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display text-2xl font-bold text-foreground mb-8">
              More Coverage
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {otherArticles.map((article) => (
                <a
                  key={article.id}
                  href={article.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <Card className="overflow-hidden h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-card">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 bg-background/90 backdrop-blur-sm text-foreground text-xs font-medium rounded-full">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-display text-lg font-bold text-foreground mb-3 group-hover:text-primary transition-colors line-clamp-2">
                        {article.title}
                        {article.category === 'Award' && <Award className="inline-block w-4 h-4 ml-2 text-amber-500" />}
                        {article.category === 'Directory' && <BookOpen className="inline-block w-4 h-4 ml-2 text-primary" />}
                      </h3>
                      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{article.source}</span>
                        {article.date && <span>{article.date}</span>}
                      </div>
                    </div>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter/Updates Section */}
      <section className="container mx-auto px-4 mb-20">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12 text-center bg-gradient-to-br from-primary/5 via-background to-primary/10 border-primary/20">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">
              Stay Updated
            </h2>
            <p className="text-muted-foreground text-lg mb-6 max-w-xl mx-auto">
              Follow Terravia Ceramics for the latest news, exhibitions, and artistic insights
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="https://www.instagram.com/terravia_ceramics" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-opacity"
              >
                Follow on Instagram
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default News;
